/**
 * Date formatting for the Seller Area. One implementation for design cards,
 * connections and anything else that shows a timestamp, so the same instant
 * never reads differently on two surfaces.
 *
 * Mirrors components/direct-sale/format-money.ts: the caller passes
 * `i18n.language`, and `Intl` does the locale work — no hand-rolled patterns
 * per language.
 */

import { DEFAULT_LANGUAGE } from '@/lib/i18n';

/** Day, month, year plus hour and minute — the "last changed" reading. */
export const DEFAULT_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

/**
 * Formats an ISO string or Date in the given language.
 *
 * Returns an empty string for anything that is not a real date (malformed
 * server value, `Invalid Date`), so a card renders without a timestamp
 * rather than showing "Invalid Date" or throwing during render. An
 * unrecognisable language tag falls back to the default language instead of
 * failing, matching how the URL parameter itself is handled.
 */
export function formatDate(
  value: string | Date,
  language: string,
  options: Intl.DateTimeFormatOptions = DEFAULT_DATE_FORMAT
): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  try {
    return new Intl.DateTimeFormat(language, options).format(date);
  } catch {
    // RangeError: structurally invalid language tag.
    return new Intl.DateTimeFormat(DEFAULT_LANGUAGE, options).format(date);
  }
}

// The Admin Area is English-only, so the language is fixed here.
const ADMIN_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
};

/** "19 Oct 2026" for the admin tables; a missing value reads as a dash. */
export function formatAdminDate(value: string | null): string {
  return (value && formatDate(value, 'en-GB', ADMIN_DATE_FORMAT)) || '—';
}

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfDay(date: Date): number {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  ).getTime();
}

function relativeTimeFormat(language: string): Intl.RelativeTimeFormat {
  const options: Intl.RelativeTimeFormatOptions = { numeric: 'auto' };
  try {
    return new Intl.RelativeTimeFormat(language, options);
  } catch {
    // RangeError: structurally invalid language tag.
    return new Intl.RelativeTimeFormat(DEFAULT_LANGUAGE, options);
  }
}

/**
 * How long ago a date lies, in the given language: "heute", "gestern",
 * "vor 3 Tagen", "vor 2 Wochen", "vor 4 Monaten", "vor 2 Jahren".
 *
 * Counts calendar days in the viewer's time zone, so anything from the same
 * day reads as today regardless of the hour. Under a week it counts days,
 * under 30 days whole weeks, then whole calendar months, and from twelve
 * months on whole years. A date in the future (clock skew between server
 * and browser) reads as today. Malformed input yields an empty string, like
 * `formatDate`.
 */
export function formatRelativeDate(
  value: string | Date,
  language: string,
  now: Date = new Date()
): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime()) || Number.isNaN(now.getTime())) {
    return '';
  }

  const format = relativeTimeFormat(language);
  // Rounded: a daylight-saving switch makes a calendar day 23 or 25 hours.
  const days = Math.max(
    0,
    Math.round((startOfDay(now) - startOfDay(date)) / DAY_MS)
  );

  if (days < 7) {
    return format.format(-days, 'day');
  }
  if (days < 30) {
    return format.format(-Math.floor(days / 7), 'week');
  }

  let months =
    (now.getFullYear() - date.getFullYear()) * 12 +
    (now.getMonth() - date.getMonth());
  if (now.getDate() < date.getDate()) {
    months -= 1;
  }
  months = Math.max(1, months);

  if (months < 12) {
    return format.format(-months, 'month');
  }
  return format.format(-Math.floor(months / 12), 'year');
}
